import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import Input from '../../components/Input';
import { useLoginMutation } from '../../store/services/api';
import { setCredentials } from '../../store/slices/authSlice';
import { useDispatch } from 'react-redux';
import type { TokenResponse } from '../../api/auth';

interface LoginErrors {
  email?: string;
  password?: string;
  general?: string;
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<LoginErrors>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: LoginErrors = {};
    let isValid = true;

    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setErrors({});
    console.log('[Login] Attempting login with email:', formData.email);
    
    try {
      const response = await login({
        email: formData.email,
        password: formData.password
      }).unwrap();
      
      console.log('[Login] Login response:', response);
      
      if (response && response.token) {
        console.log('[Login] Setting credentials with token');
        dispatch(setCredentials({ 
          token: response.token,
          user: response.user || null
        }));
        
        const from = location.state?.from?.pathname || '/dashboard';
        console.log('[Login] Redirecting to:', from);
        navigate(from, { replace: true });
      } else {
        console.error('[Login] No token in response');
        setErrors(prev => ({ ...prev, general: 'Login failed. Please try again.' }));
      }
    } catch (err: any) {
      console.error('[Login] Login error:', err);
      
      if (err?.data?.message?.toLowerCase().includes('email')) {
        setErrors(prev => ({ ...prev, email: err.data.message }));
      } else if (err?.data?.message?.toLowerCase().includes('password')) {
        setErrors(prev => ({ ...prev, password: err.data.message }));
      } else {
        setErrors(prev => ({ ...prev, general: err?.data?.message || 'Login failed. Please try again.' }));
      }
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-white">
      <div className="w-full max-w-md px-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to your account
          </p>
        </div>

        {errors.general && (
          <div className="mb-4 bg-red-50 p-3 rounded-md text-red-600 text-sm">
            {errors.general}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="relative">
            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              error={errors.email}
              className="pl-10"
              disabled={isLoading}
            />
            <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-[34px]" />
          </div>

          <div className="relative">
            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              error={errors.password}
              className="pl-10"
              disabled={isLoading}
            />
            <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-[34px]" />
          </div>

          <button
            type="submit"
            className="w-full py-2.5 px-4 mt-4 border border-transparent rounded-md text-sm font-medium text-white bg-[#FF4444] hover:bg-[#FF3333] transition-colors disabled:bg-opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>

          <div className="flex items-center justify-between mt-4">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-[#FF4444] hover:text-[#FF3333]"
            >
              Forgot password?
            </Link>
            <Link
              to="/register"
              className="text-sm font-medium text-[#FF4444] hover:text-[#FF3333]"
            >
              Create account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;